// src/services/liveVideoService.ts
import unifiedAuth from '../utils/unifiedAuth';
import { config } from '../config';

export interface LiveVideo {
  id: string;
  slug: string;
  title: string;
  description: string;
  short_description?: string;
  thumbnail?: string;
  video_file?: string;
  /**
   * HLS or PROGRESSIVE, straight from the backend. The hero cannot guess: a Bunny Stream
   * URL puts the directory token in a path segment ahead of the filename, so checking for
   * a ".m3u8" suffix is exactly the inference that breaks when a URL changes shape.
   */
  delivery_format?: 'HLS' | 'PROGRESSIVE';
  secure_thumbnail_url?: string;
  secure_video_url?: string;
  is_live: boolean;
  live_status?: string;
  viewer_count?: number;
  current_viewers?: number;
  like_count?: number;
  comment_count?: number;
  total_views?: number;
  is_liked?: boolean;
  is_bookmarked?: boolean;
  duration?: number;
  duration_formatted?: string;
  author_username?: string;
  host_name?: string;
  tags?: string;
  created_at: string;
  start_time?: string;
  scheduled_start_time?: string;
  author?: {
    username: string;
    first_name: string;
    last_name: string;
  };
}

export interface CreateLiveVideoData {
  title: string;
  description: string;
  thumbnail?: File | null;
  videoFile?: File | null; // For upload mode
  mode?: 'live' | 'upload';
}

export interface LiveVideoComment {
  id: string;
  user_username: string;
  user: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
  message: string;
  created_at: string;
  timestamp: string;
  is_moderator: boolean;
  is_hidden: boolean;
  stream_time?: number;
}

export interface CreateLiveVideoCommentData {
  message: string;
}

const API_BASE_URL = config.apiBaseUrl;

// Demo mode - set to true to use localStorage for testing
const DEMO_MODE = import.meta.env.VITE_LIVE_VIDEO_DEMO === 'true' || false; // Disable demo by default

class LiveVideoService {
  private getAuthHeaders(includeContentType = true) {
    // Authorization is attached by unifiedAuth.fetch, which refreshes on expiry.
    const headers: Record<string, string> = {};
    
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    
    return headers;
  }

  // Helper method to fetch comment count for a content
  private async fetchCommentCount(contentId: string): Promise<number> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/comments/content/LIVE_VIDEO/${contentId}?page=0&size=1`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.totalElements || 0;
      }
      return 0;
    } catch {
      return 0;
    }
  }

  async createLiveVideo(data: CreateLiveVideoData): Promise<LiveVideo> {
    // Check authentication first
    const token = unifiedAuth.getAccessToken();
    if (!token) {
      throw new Error('You must be logged in to create a live video');
    }

    // Demo mode for testing UI without backend
    if (DEMO_MODE) {
      const demoVideo: LiveVideo = {
        id: Date.now().toString(),
        slug: `demo-video-${Date.now()}`,
        title: data.title,
        description: data.description,
        thumbnail: data.thumbnail ? URL.createObjectURL(data.thumbnail) : undefined,
        is_live: true,
        live_status: 'live',
        viewer_count: Math.floor(Math.random() * 50) + 1,
        current_viewers: Math.floor(Math.random() * 50) + 1,
        created_at: new Date().toISOString(),
        start_time: new Date().toISOString(),
        scheduled_start_time: new Date().toISOString(),
        author: {
          username: 'demo_user',
          first_name: 'Demo',
          last_name: 'User'
        }
      };
      
      // Store in localStorage for demo
      localStorage.setItem('currentLiveVideo', JSON.stringify(demoVideo));
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return demoVideo;
    }

    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('description', data.description);
    
    // Handle upload mode vs live mode
    if (data.mode === 'upload' && data.videoFile) {
      // For uploaded videos, set them as ready to play
      formData.append('video_file', data.videoFile);
      formData.append('live_status', 'live'); // Make uploaded videos appear as "live" content
      // Set immediate availability for uploads
      const now = new Date();
      formData.append('scheduled_start_time', now.toISOString());
    } else {
      // For live videos, schedule for immediate start
      const now = new Date();
      const startTime = new Date(now.getTime() + 5000); // 5 seconds from now
      const endTime = new Date(now.getTime() + 2 * 60 * 60 * 1000); // 2 hours from now
      formData.append('scheduled_start_time', startTime.toISOString());
      formData.append('scheduled_end_time', endTime.toISOString());
    }
    
    if (data.thumbnail) {
      formData.append('thumbnail', data.thumbnail);
    }

    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/live-videos/`, {
        method: 'POST',
        headers: this.getAuthHeaders(false), // Don't include Content-Type for FormData
        body: formData,
      });

      if (response.status === 401) {
        throw new Error('You must be logged in to create a live video. Please sign in and try again.');
      }

      if (response.status === 404) {
        throw new Error('Live video feature is not yet available on the backend. Please contact your administrator to implement the live video API endpoints.');
      }

      if (!response.ok) {
        const _errorData = await response.json().catch(() => ({}));
        throw new Error(_errorData.message || _errorData.detail || 'Failed to create live video');
      }

      const liveVideo = await response.json();
      
      // Check if we have a slug to start the stream
      if (!liveVideo.slug && !liveVideo.id) {
        throw new Error('Live video created but missing identification field');
      }
      
      // Only try to start live stream for actual live videos (not uploads)
      if (data.mode !== 'upload') {
        // Immediately start the live video using slug or id as fallback
        const identifier = liveVideo.slug || liveVideo.id;
        await this.startLiveVideo(identifier);
      }
      
      // Return the video with updated status
      return { ...liveVideo, live_status: 'live', is_live: true };
    } catch (_error) {
      if (_error instanceof TypeError && _error.message.includes('fetch')) {
        throw new Error('Unable to connect to the backend server. Please check your connection and try again.');
      }
      throw _error;
    }
  }

  async startLiveVideo(identifier: string): Promise<void> {
    const url = `${API_BASE_URL}/live-video/live-videos/${identifier}/start_stream/`;
    
    const response = await unifiedAuth.fetch(url, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const _errorData = await response.json().catch(() => ({}));
      throw new Error(_errorData.message || _errorData.detail || 'Failed to start live video');
    }
  }

  async getCurrentLiveVideo(): Promise<LiveVideo | null> {
    // Demo mode for testing UI without backend
    if (DEMO_MODE) {
      const storedVideo = localStorage.getItem('currentLiveVideo');
      if (storedVideo) {
        try {
          const video = JSON.parse(storedVideo);
          // Check if video is still "live" (demo videos expire after 30 minutes)
          const startTime = new Date(video.start_time);
          const now = new Date();
          const diffMinutes = (now.getTime() - startTime.getTime()) / (1000 * 60);
          
          if (diffMinutes < 30) {
            // Update viewer count for demo
            video.viewer_count = Math.floor(Math.random() * 100) + 10;
            video.current_viewers = video.viewer_count;
            return video;
          } else {
            // Demo video expired
            localStorage.removeItem('currentLiveVideo');
            return null;
          }
        } catch {
          localStorage.removeItem('currentLiveVideo');
          return null;
        }
      }
      return null;
    }

    try {
      // Try the new dedicated live video endpoint first
      let response, data;
      
      try {
        response = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/current`, {
          method: 'GET',
          headers: this.getAuthHeaders(),
        });
        
        if (response.ok) {
          data = await response.json();
          if (data.data) {
            const liveContent = data.data;
            
            // Fetch comment count for the live video
            const commentCount = await this.fetchCommentCount(liveContent.id);
            
            // Transform backend Content entity to LiveVideo format
            return {
              id: liveContent.id,
              slug: liveContent.slug || liveContent.id,
              title: liveContent.title,
              description: liveContent.description,
              live_status: liveContent.liveStatus || 'LIVE',
              video_file: liveContent.mediaUrl,
              // Both names hold the same value and always did. It is now the playable one:
              // the endpoint returns ContentDetailResponse, so this is the Bunny Stream
              // manifest when the content has been migrated and a signed URL when it has
              // not - rather than the raw stored file, which answers 403 to everything.
              secure_video_url: liveContent.mediaUrl,
              delivery_format: liveContent.deliveryFormat,
              secure_thumbnail_url: liveContent.thumbnailUrl,
              current_viewers: liveContent.currentViewers || 0,
              total_views: liveContent.viewCount || 0,
              like_count: liveContent.likeCount || 0,
              comment_count: commentCount,
              is_live: liveContent.isLive || true,
              start_time: liveContent.actualStartTime || liveContent.createdAt,
              scheduled_start_time: liveContent.scheduledStartTime,
              created_at: liveContent.createdAt
            };
          }
        }
      } catch {
        // Fallback to old endpoint if new one fails
      }
      
      // Fallback: Use the general content endpoint
      response = await unifiedAuth.fetch(`${API_BASE_URL}/content/live?page=0&size=1`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch current live video');
      }

      data = await response.json();
      
      // Extract the first live video from the response
      if (data.data && data.data.content && data.data.content.length > 0) {
        const liveContent = data.data.content[0];
        
        // Fetch comment count for the live video
        const commentCount = await this.fetchCommentCount(liveContent.id);
        
        // Transform backend Content entity to LiveVideo format
        return {
          id: liveContent.id,
          slug: liveContent.slug || liveContent.id,
          title: liveContent.title,
          description: liveContent.description,
          live_status: liveContent.liveStatus || 'LIVE',
          video_file: liveContent.mediaUrl,
          secure_video_url: liveContent.mediaUrl,
          delivery_format: liveContent.deliveryFormat,
          secure_thumbnail_url: liveContent.thumbnailUrl,
          current_viewers: liveContent.currentViewers || 0,
          total_views: liveContent.viewCount || 0,
          like_count: liveContent.likeCount || 0,
          comment_count: commentCount,
          is_live: liveContent.isLive || true,
          start_time: liveContent.actualStartTime || liveContent.createdAt,
          scheduled_start_time: liveContent.scheduledStartTime,
          created_at: liveContent.createdAt
        };
      }
      
      return null;
    } catch (_error) {
      // Silently handle connection errors and 404s
      if (_error instanceof TypeError && _error.message.includes('fetch')) {
        // Network error - endpoints probably don't exist
        return null;
      }
      return null; // Return null on any error to avoid breaking the UI
    }
  }

  async getLiveVideoBySlug(slug: string): Promise<LiveVideo | null> {
    // This method retrieves a single live video by slug
    // It triggers the backend retrieve method which creates watch interactions
    try {
      // First try to find by slug in the content endpoint
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/content/${slug}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch live video');
      }

      const data = await response.json();
      
      // Transform backend Content entity to LiveVideo format
      if (data.data && data.data.content) {
        const liveContent = data.data.content;
        
        // Only return if it's a LIVE_VIDEO type
        if (liveContent.contentType === 'LIVE_VIDEO') {
          // Fetch comment count for the live video
          const commentCount = await this.fetchCommentCount(liveContent.id);
          
          return {
            id: liveContent.id,
            slug: liveContent.slug || liveContent.id,
            title: liveContent.title,
            description: liveContent.description,
            live_status: liveContent.liveStatus || (liveContent.isLive ? 'LIVE' : 'ENDED'),
            video_file: liveContent.mediaUrl,
            secure_video_url: liveContent.mediaUrl,
            delivery_format: liveContent.deliveryFormat,
            secure_thumbnail_url: liveContent.thumbnailUrl,
            current_viewers: liveContent.currentViewers || 0,
            like_count: liveContent.likeCount || 0,
            comment_count: commentCount,
            is_live: liveContent.isLive || false,
            start_time: liveContent.actualStartTime || liveContent.createdAt,
            scheduled_start_time: liveContent.scheduledStartTime,
            created_at: liveContent.createdAt
          };
        }
      }
      
      return null;
    } catch (_error) {
      if (_error instanceof TypeError && _error.message.includes('fetch')) {
        throw new Error('Unable to connect to the backend server');
      }
      throw _error;
    }
  }

  async getMyWatchedLiveVideos(page = 1): Promise<{
    results: LiveVideo[];
    count: number;
    has_next: boolean;
    has_previous: boolean;
  }> {
    // Get live videos watched by the current user
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/live-videos/my_watched/?page=${page}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch watched live videos');
      }

      return response.json();
    } catch (_error) {
      if (_error instanceof TypeError && _error.message.includes('fetch')) {
        throw new Error('Unable to connect to the backend server');
      }
      throw _error;
    }
  }

  async endLiveVideo(id: string): Promise<void> {
    // Demo mode for testing UI without backend
    if (DEMO_MODE) {
      const storedVideo = localStorage.getItem('currentLiveVideo');
      if (storedVideo) {
        try {
          const video = JSON.parse(storedVideo);
          if (video.id === id) {
            localStorage.removeItem('currentLiveVideo');
          }
        } catch {
          localStorage.removeItem('currentLiveVideo');
        }
      }
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return;
    }

    const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/live-videos/${id}/end/`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const _errorData = await response.json().catch(() => ({}));
      throw new Error(_errorData.message || _errorData.detail || 'Failed to end live video');
    }
  }

  // New Live Video Methods using dedicated endpoints
  async getUpcomingLiveVideos(page = 0, size = 20): Promise<{
    content: LiveVideo[];
    totalElements: number;
    totalPages: number;
    last: boolean;
    first: boolean;
  }> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/upcoming?page=${page}&size=${size}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch upcoming live videos');
      }

      const data = await response.json();
      
      if (data.data && data.data.content) {
        const transformedContent = data.data.content.map((liveContent: { 
          id: string; 
          slug?: string; 
          title: string; 
          description: string; 
          liveStatus?: string; 
          deliveryFormat?: 'HLS' | 'PROGRESSIVE'; 
          mediaUrl: string; 
          thumbnailUrl: string; 
          viewCount?: number; 
          likeCount?: number; 
          scheduledStartTime?: string; 
          actualStartTime?: string; 
          createdAt: string;
        }) => ({
          id: liveContent.id,
          slug: liveContent.slug || liveContent.id,
          title: liveContent.title,
          description: liveContent.description,
          live_status: liveContent.liveStatus || 'SCHEDULED',
          video_file: liveContent.mediaUrl,
          secure_video_url: liveContent.mediaUrl,
          delivery_format: liveContent.deliveryFormat,
          secure_thumbnail_url: liveContent.thumbnailUrl,
          current_viewers: 0, // Not live yet
          total_views: liveContent.viewCount || 0,
          like_count: liveContent.likeCount || 0,
          comment_count: 0,
          is_live: false,
          start_time: liveContent.actualStartTime,
          scheduled_start_time: liveContent.scheduledStartTime,
          created_at: liveContent.createdAt
        }));

        return {
          content: transformedContent,
          totalElements: data.data.totalElements || transformedContent.length,
          totalPages: data.data.totalPages || 1,
          last: data.data.last !== false,
          first: data.data.first !== false
        };
      }

      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        last: true,
        first: true
      };
    } catch (_error) {
      throw new Error('Failed to fetch upcoming live videos');
    }
  }

  async getPastLiveVideos(page = 0, size = 20): Promise<{
    content: LiveVideo[];
    totalElements: number;
    totalPages: number;
    last: boolean;
    first: boolean;
  }> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/past?page=${page}&size=${size}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch past live videos');
      }

      const data = await response.json();
      
      if (data.data && data.data.content) {
        const transformedContent = data.data.content.map((liveContent: { 
          id: string; 
          slug?: string; 
          title: string; 
          description: string; 
          liveStatus?: string; 
          deliveryFormat?: 'HLS' | 'PROGRESSIVE'; 
          mediaUrl: string; 
          thumbnailUrl: string; 
          viewCount?: number; 
          likeCount?: number; 
          scheduledStartTime?: string; 
          actualStartTime?: string; 
          createdAt: string;
        }) => ({
          id: liveContent.id,
          slug: liveContent.slug || liveContent.id,
          title: liveContent.title,
          description: liveContent.description,
          live_status: liveContent.liveStatus || 'ENDED',
          video_file: liveContent.mediaUrl,
          secure_video_url: liveContent.mediaUrl,
          delivery_format: liveContent.deliveryFormat,
          secure_thumbnail_url: liveContent.thumbnailUrl,
          current_viewers: 0, // Stream ended
          total_views: liveContent.viewCount || 0,
          like_count: liveContent.likeCount || 0,
          comment_count: 0,
          is_live: false,
          start_time: liveContent.actualStartTime,
          scheduled_start_time: liveContent.scheduledStartTime,
          created_at: liveContent.createdAt
        }));

        return {
          content: transformedContent,
          totalElements: data.data.totalElements || transformedContent.length,
          totalPages: data.data.totalPages || 1,
          last: data.data.last !== false,
          first: data.data.first !== false
        };
      }

      return {
        content: [],
        totalElements: 0,
        totalPages: 0,
        last: true,
        first: true
      };
    } catch (_error) {
      throw new Error('Failed to fetch past live videos');
    }
  }

  async getLiveVideoStats(videoId: string): Promise<{
    viewCount: number;
    currentViewers: number;
    likeCount: number;
    commentCount: number;
    isLive: boolean;
    liveStatus: string;
  } | null> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/${videoId}/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      
      if (data.data) {
        return {
          viewCount: data.data.viewCount || 0,
          currentViewers: data.data.currentViewers || 0,
          likeCount: data.data.likeCount || 0,
          commentCount: data.data.commentCount || 0,
          isLive: data.data.isLive || false,
          liveStatus: data.data.liveStatus || 'OFFLINE'
        };
      }

      return null;
    } catch (_error) {
      return null;
    }
  }

  async recordLiveVideoView(videoId: string): Promise<{ success: boolean; viewId?: string; viewCount?: number }> {
    try {
      // Try the dedicated live video view endpoint first
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/${videoId}/view`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          viewId: data.data?.viewId,
          viewCount: data.data?.viewCount
        };
      }
      
      // Fallback to general content endpoint if live video specific fails
      const fallbackResponse = await unifiedAuth.fetch(`${API_BASE_URL}/content/${videoId}/views`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });

      if (fallbackResponse.ok) {
        const fallbackData = await fallbackResponse.json();
        return {
          success: true,
          viewId: fallbackData.data?.viewId,
          viewCount: fallbackData.data?.viewCount
        };
      }

      return { success: false };
    } catch (_error) {
      return { success: false };
    }
  }

    // Live Video Comment Methods
  async getLiveVideoComments(contentId: string, page = 1, pageSize = 50): Promise<{
    results: LiveVideoComment[];
    count: number;
    has_next: boolean;
    has_previous: boolean;
  }> {
    try {
      // Use the comments endpoint with LIVE_VIDEO content type
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/comments/content/LIVE_VIDEO/${contentId}?page=${page - 1}&size=${pageSize}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }

      const data = await response.json();
      
      // Transform the response to match expected format
      return {
        results: data.content || [],
        count: data.totalElements || 0,
        has_next: !data.last,
        has_previous: !data.first
      };
    } catch (_error) {
      throw new Error('Failed to fetch comments');
    }
  }

  async createLiveVideoComment(contentId: string, data: CreateLiveVideoCommentData): Promise<LiveVideoComment> {
    try {
      // Use the content interaction endpoint for commenting
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/content/${contentId}/interact`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          interaction_type: 'comment',
          message: data.message
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create comment');
      }

      return response.json();
    } catch (_error) {
      throw new Error('Failed to create comment');
    }
  }

  async interactWithLiveVideo(slug: string, interactionType: 'like' | 'bookmark' | 'join' | 'leave'): Promise<void> {
    try {
      const response = await unifiedAuth.fetch(`${API_BASE_URL}/live-video/live-videos/${slug}/interact/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          interaction_type: interactionType
        }),
      });

      if (!response.ok) {
        const _errorData = await response.json().catch(() => ({}));
        throw new Error(_errorData.detail || 'Failed to interact with live video');
      }
    } catch (_error) {
      throw new Error(`Failed to interact with live video: ${_error instanceof Error ? _error.message : 'Unknown error'}`);
    }
  }

  async addLiveVideoComment(slug: string, message: string): Promise<LiveVideoComment> {
    return this.createLiveVideoComment(slug, { message });
  }
}

export default new LiveVideoService();
